import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

function FFMPilotes() {
  const [pilotes, setPilotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPilote, setEditingPilote] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [formData, setFormData] = useState({ prenom: '', nom: '', email: '', telephone: '' });
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    loadPilotes();
  }, []);

  const loadPilotes = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('ffm_pilotes')
      .select('*')
      .order('nom', { ascending: true });

    if (error) {
      console.error('Erreur chargement pilotes FFM:', error);
    } else {
      setPilotes(data || []);
    }
    setLoading(false);
  };

  const openModal = (pilote = null) => {
    setErreur('');
    if (pilote) {
      setEditingPilote(pilote);
      setFormData({
        prenom: pilote.prenom,
        nom: pilote.nom,
        email: pilote.email,
        telephone: pilote.telephone || ''
      });
    } else {
      setEditingPilote(null);
      setFormData({ prenom: '', nom: '', email: '', telephone: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPilote(null);
    setFormData({ prenom: '', nom: '', email: '', telephone: '' });
    setErreur('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErreur('');

    if (!formData.prenom.trim() || !formData.nom.trim()) {
      setErreur('Le prénom et le nom sont obligatoires.');
      return;
    }
    if (!formData.email.trim()) {
      setErreur('L\'email est obligatoire.');
      return;
    }

    const payload = {
      prenom: formData.prenom.trim(),
      nom: formData.nom.trim(),
      email: formData.email.trim().toLowerCase(),
      telephone: formData.telephone.trim() || null
    };

    if (editingPilote) {
      const { error } = await supabase
        .from('ffm_pilotes')
        .update(payload)
        .eq('id', editingPilote.id);

      if (error) {
        if (error.code === '23505') {
          setErreur('Cet email est déjà utilisé par un autre pilote.');
        } else {
          setErreur('Erreur lors de la modification.');
          console.error(error);
        }
        return;
      }
    } else {
      const { error } = await supabase
        .from('ffm_pilotes')
        .insert([payload]);

      if (error) {
        if (error.code === '23505') {
          setErreur('Cet email est déjà utilisé par un autre pilote.');
        } else {
          setErreur('Erreur lors de la création.');
          console.error(error);
        }
        return;
      }
    }

    loadPilotes();
    closeModal();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce pilote ?')) return;
    const { error } = await supabase
      .from('ffm_pilotes')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erreur suppression:', error);
    } else {
      loadPilotes();
    }
  };

  // Filtrage par recherche
  const pilotesFiltres = pilotes.filter(p => {
    const q = recherche.toLowerCase();
    return (
      p.nom.toLowerCase().includes(q) ||
      p.prenom.toLowerCase().includes(q) ||
      p.email.toLowerCase().includes(q) ||
      (p.telephone || '').includes(q)
    );
  });

  // Initiales pour l'avatar
  const initiales = (p) =>
    `${p.prenom.charAt(0)}${p.nom.charAt(0)}`.toUpperCase();

  // ─── Styles ──────────────────────────────────────────────────────────────────

  const styleBtnPrimary = {
    padding: '10px 20px',
    backgroundColor: '#003189',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px'
  };

  const styleBtnSecondary = {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#6c757d',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500'
  };

  const styleBtnDanger = {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#dc3545',
    border: '1px solid #dc3545',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500'
  };

  const styleInput = {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const styleLabel = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#495057',
    marginBottom: '6px'
  };

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#212529' }}>
            Pilotes FFM
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6c757d' }}>
            {pilotes.length} pilote{pilotes.length > 1 ? 's' : ''} enregistré{pilotes.length > 1 ? 's' : ''}
          </p>
        </div>
        <button style={styleBtnPrimary} onClick={() => openModal()}>
          + Nouveau pilote
        </button>
      </div>

      {/* Barre de recherche */}
      {pilotes.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <input
            style={{ ...styleInput, maxWidth: '360px' }}
            type="text"
            placeholder="Rechercher un pilote..."
            value={recherche}
            onChange={e => setRecherche(e.target.value)}
          />
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div style={{ textAlign: 'center', color: '#6c757d', padding: '60px' }}>
          Chargement...
        </div>
      ) : pilotesFiltres.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#6c757d', padding: '60px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🏁</div>
          <div style={{ fontSize: '16px', fontWeight: '600' }}>
            {pilotes.length === 0 ? 'Aucun pilote créé' : 'Aucun résultat'}
          </div>
          <div style={{ fontSize: '13px', marginTop: '6px' }}>
            {pilotes.length === 0 ? 'Cliquez sur "+ Nouveau pilote" pour commencer.' : 'Essayez une autre recherche.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {pilotesFiltres.map(pilote => (
            <div key={pilote.id} style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e9ecef',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              {/* Avatar initiales */}
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: '#e8f0fe',
                color: '#003189',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                fontSize: '15px',
                flexShrink: 0
              }}>
                {initiales(pilote)}
              </div>

              {/* Infos */}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '700', fontSize: '15px', color: '#212529' }}>
                  {pilote.prenom} {pilote.nom}
                </div>
                <div style={{ display: 'flex', gap: '16px', marginTop: '4px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: '#6c757d' }}>
                    ✉️ {pilote.email}
                  </span>
                  {pilote.telephone && (
                    <span style={{ fontSize: '13px', color: '#6c757d' }}>
                      📞 {pilote.telephone}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={styleBtnSecondary} onClick={() => openModal(pilote)}>
                  Modifier
                </button>
                <button style={styleBtnDanger} onClick={() => handleDelete(pilote.id)}>
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── MODAL ─────────────────────────────────────────────────────────────── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff', borderRadius: '16px',
            padding: '32px', width: '100%', maxWidth: '480px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 24px', fontSize: '18px', fontWeight: '700', color: '#212529' }}>
              {editingPilote ? 'Modifier le pilote' : 'Nouveau pilote'}
            </h3>

            <form onSubmit={handleSubmit}>
              {/* Prénom + Nom */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                <div>
                  <label style={styleLabel}>Prénom *</label>
                  <input
                    style={styleInput}
                    type="text"
                    placeholder="Prénom"
                    value={formData.prenom}
                    onChange={e => setFormData(prev => ({ ...prev, prenom: e.target.value }))}
                    autoFocus
                  />
                </div>
                <div>
                  <label style={styleLabel}>Nom *</label>
                  <input
                    style={styleInput}
                    type="text"
                    placeholder="Nom"
                    value={formData.nom}
                    onChange={e => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                  />
                </div>
              </div>

              {/* Email */}
              <div style={{ marginBottom: '16px' }}>
                <label style={styleLabel}>Email *</label>
                <input
                  style={styleInput}
                  type="email"
                  placeholder="email@exemple.com"
                  value={formData.email}
                  onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>

              {/* Téléphone */}
              <div style={{ marginBottom: '24px' }}>
                <label style={styleLabel}>Téléphone <span style={{ color: '#adb5bd', fontWeight: '400' }}>(optionnel)</span></label>
                <input
                  style={styleInput}
                  type="tel"
                  placeholder="06 00 00 00 00"
                  value={formData.telephone}
                  onChange={e => setFormData(prev => ({ ...prev, telephone: e.target.value }))}
                />
              </div>

              {/* Note info */}
              <div style={{
                padding: '10px 14px', backgroundColor: '#f8f9fa',
                borderRadius: '8px', fontSize: '12px', color: '#6c757d',
                marginBottom: '20px', borderLeft: '3px solid #003189'
              }}>
                Les pilotes n'ont pas accès au backoffice ni au dashboard FFM. Ils reçoivent uniquement les emails de validation BAT.
              </div>

              {/* Erreur */}
              {erreur && (
                <div style={{
                  padding: '10px 14px', backgroundColor: '#f8d7da',
                  color: '#721c24', borderRadius: '8px', fontSize: '13px', marginBottom: '16px'
                }}>
                  {erreur}
                </div>
              )}

              {/* Boutons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" style={styleBtnSecondary} onClick={closeModal}>
                  Annuler
                </button>
                <button type="submit" style={styleBtnPrimary}>
                  {editingPilote ? 'Enregistrer' : 'Créer le pilote'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FFMPilotes;
